import React from 'react';
import { Tabs } from 'antd';
import { useBooking } from '../../../../../../../contexts/ticketBookingContext/TicketBookingContext';
import { approveReserve } from '../../../../../../../../services/customer/ticketBookingServices';
import selectPassengersFormSet from '../../../../../../../../utils/functions/booking/selectPassengersFormSet';
import reserveSeatsWithAdditionalServices
  from '../../../../../../../../utils/functions/booking/reserveSeatsWithAdditionalServices';
import purchaseBookedTickets from '../../../../../../../../utils/functions/booking/purchaseBookedTickets';
import SelectNavigationTabItems from '../bookingTypeNavigationTabItems/SelectNavigationTabItems';
import BookingNavigationButtons from '../BookingNavigationButtons';
import useTranslation from '../../../../../../../hooks/translation/useTranslation';
import ChosenRideDetails
  from '../../../../../../components/drawers/ticketSearchDrawers/ticketSearchFormDrawer/components/ChosenRideDetails';
import checkPassengers from '../../../../../../../../utils/functions/booking/checkPassengers';

function SelectBookingNavigation({ outline = true }) {
  const { trans } = useTranslation();

  const {
    configInfo,
  } = useTranslation();
  const {
    departureChosenSeats,
    bookingData,
    departurePassengersForm,
    returnPassengersForm,
    returnChosenSeats,
    statement,
    activeStep,
    setActiveStep,
    setStatement,
    services,
    paymentMethodForm,
    ridesCarriagesInfo,
    cancelRef,
    setIsNextDisabled,
  } = useBooking();

  const isReturnWay = Boolean(bookingData?.return?.id);
  const lastStep = isReturnWay ? 5 : 3;

  // Define handlers for each step in the process
  const nextStepHandlers = {
    1: async () => {
      if (isReturnWay) {
        setActiveStep(2);
      } else {
        if (!(departureChosenSeats?.length > 0)) {
          return;
        }
        const res = await approveReserve({
          id: departureChosenSeats?.[0]?.statement_id,
          guid: ridesCarriagesInfo?.statement?.statementGuid,
        });
        if (res.status === 'success') {
          setStatement(res.data);
          await selectPassengersFormSet({
            passengersForm: departurePassengersForm,
            chosenSeats: departureChosenSeats,
            statement: res.data,
          });
          setActiveStep(2);
        }
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    2: async () => {
      if (isReturnWay) {
        if (!(returnChosenSeats?.length > 0)) {
          return;
        }
        const res = await approveReserve({
          id: departureChosenSeats?.[0]?.statement_id,
          guid: ridesCarriagesInfo?.statement?.statementGuid,
        });
        if (res.status === 'success') {
          setStatement(res.data);
          await selectPassengersFormSet({
            passengersForm: departurePassengersForm,
            chosenSeats: departureChosenSeats,
            statement: res.data,
          });
          await selectPassengersFormSet({
            passengersForm: returnPassengersForm,
            chosenSeats: returnChosenSeats,
            statement: res.data,
            returnWay: true,
          });
          setActiveStep(3);
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      } else {
        checkPassengers({
          form: departurePassengersForm,
          onSuccess: async () => {
            await reserveSeatsWithAdditionalServices({
              departurePassengersForm,
              bookingData,
              statement,
              services,
              nextStep: () => {
                setActiveStep(3);
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              },
              setStatement,
            });
          },
          trans,
        });
      }
    },
    3: async () => {
      if (isReturnWay) {
        await departurePassengersForm.validateFields();
        checkPassengers({
          form: departurePassengersForm,
          onSuccess: async () => {
            setActiveStep(4);
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          },
          trans,
        });
      } else {
        cancelRef.current = 'keep';
        purchaseBookedTickets({
          id: statement.id,
          guid: statement.statementGuid,
          countryCode: configInfo.countryCode,
          payment_card: paymentMethodForm.getFieldValue('payment_card'),
          payment_bank: paymentMethodForm.getFieldValue('payment_bank'),
          trans,
          setIsNextDisabled,
        });
      }
    },
    4: async () => {
      await returnPassengersForm.validateFields();
      checkPassengers({
        form: returnPassengersForm,
        onSuccess: async () => {
          await reserveSeatsWithAdditionalServices({
            departurePassengersForm,
            returnPassengersForm,
            bookingData,
            statement,
            services,
            nextStep: () => {
              setActiveStep(5);
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            },
            setStatement,
          });
        },
        trans,
      });
    },
    5: async () => {
      cancelRef.current = 'keep';
      purchaseBookedTickets({
        id: statement.id,
        guid: statement.statementGuid,
        countryCode: configInfo.countryCode,
        payment_card: paymentMethodForm.getFieldValue('payment_card'),
        payment_bank: paymentMethodForm.getFieldValue('payment_bank'),
        trans,
        setIsNextDisabled,
      });
    },
  };

  const handleNextStep = async () => {
    try {
      if (activeStep === 2) {
        // Validate form fields before proceeding to the next step
        await departurePassengersForm.validateFields();
      }
      // Call the appropriate step handler based on the current step
      nextStepHandlers[activeStep]();
    } catch {
      // Handle errors if any
    }
  };

  const handlePreviousStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <div
      className={`flex flex-col grow lg:p-8 rounded-2xl bg-white ${outline && 'outline-border'} !sticky top-[300px]`}
    >
      {(bookingData?.departure || bookingData?.return) && (
        <div className="mb-4 hidden lg:flex flex-col gap-4">
          {bookingData?.departure && <ChosenRideDetails chosenRide={bookingData?.departure} />}
          {bookingData?.return && <ChosenRideDetails chosenRide={bookingData?.return} />}
        </div>
      )}
      <span className={activeStep === lastStep ? 'max-h-[200px] lg:max-h-full overflow-y-auto lg:overflow-y-hidden' : 'max-h-full overflow-y-hidden'}>
        <Tabs className="no-tablist" items={SelectNavigationTabItems()} activeKey={activeStep} />
      </span>
      <BookingNavigationButtons
        handlePreviousStep={handlePreviousStep}
        handleNextStep={handleNextStep}
        activeStep={activeStep}
        isNextDisabled={departureChosenSeats.length < 1
              || (isReturnWay && activeStep > 1 && returnChosenSeats.length < 1
              )}
        lastStep={lastStep}
      />
    </div>
  );
}

export default SelectBookingNavigation;
